import type { JSX } from "react";
import { useEffect, useRef } from "react";

/** Decorative bright points layered over the static `.atlas-specks` gradients. */
const TWINKLE_COUNT = 48;

/** Glint cycle bounds, ms: short enough that a point reads as a spark rather than
 *  a slow fade. */
const TWINKLE_MIN_MS = 900;
const TWINKLE_MAX_MS = 1800;

/** Drift cycle bounds, ms, and amplitude bounds, px: the slow second beat that
 *  keeps the field alive between glints. */
const DRIFT_MIN_MS = 14000;
const DRIFT_MAX_MS = 26000;
const DRIFT_MIN_PX = 8;
const DRIFT_MAX_PX = 20;

const sampleBetween = (min: number, max: number): number => min + Math.random() * (max - min);

/** A signed offset in the drift band, either direction. */
const sampleDrift = (): number =>
  sampleBetween(DRIFT_MIN_PX, DRIFT_MAX_PX) * (Math.random() < 0.5 ? -1 : 1);

/**
 * Deterministic per index so a re-render never reshuffles the sky: every third
 * point carries the gradient field's own blue-white, every fifth is a
 * first-magnitude star (a larger box and a wider halo, and therefore a larger
 * sweep for the same scale pulse).
 */
const twinkleClass = (i: number): string => {
  const tinted = i % 3 === 1 ? "atlas-twinkle atlas-twinkle--ice" : "atlas-twinkle";
  return i % 5 === 0 ? `${tinted} atlas-twinkle--bright` : tinted;
};

/** The two animations one point owns: the looping drift and the current glint. */
type PointAnimations = { drift: Animation; twinkle: Animation };

/**
 * The atlas sky: one deterministic tiled gradient field (its shape lives in
 * home.css, which also owns the radii and alphas) plus the twinkle points this
 * component animates. Both routes that paint the atlas background — the
 * homepage's sticky stage and the city guide's fixed stage — render this one
 * implementation, so the sky cannot drift between them.
 *
 * Two compositor animations per point. The glint is the fast beat: one Web
 * Animation per point, resampled on every cycle, so no two points ever share a
 * rhythm. The drift is the slow one: a single looping animation created once per
 * activation, walking the point a few px off its authored position and back, so
 * the field keeps moving between glints and the drift does not reset every time
 * the glint chain resamples. The two animate disjoint properties — `translate`
 * for the drift, `opacity` and `scale` for the glint — so neither clobbers the
 * other and `transform` stays free for the CSS centring pair. No timers, no rAF
 * loop, no React state — the animations live in the compositor and the only
 * thing this effect owns is their lifecycle.
 *
 * Glints reach full white and swell to 1.4x; the delay window is wide enough
 * that only a fraction of the 48 points glint at once, so the dense field reads
 * as stars sparking rather than a field flickering.
 *
 * Without `Element.animate` the CSS gradient field is the whole star layer,
 * exactly as it was before.
 */
export function StarField(): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const layer = ref.current;
    if (!layer || typeof layer.animate !== "function") return undefined;
    const points = Array.from(layer.querySelectorAll<HTMLElement>(".atlas-twinkle"));
    if (points.length === 0) return undefined;

    const reducedQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const running = new Map<HTMLElement, PointAnimations>();
    let active = false;

    // One glint, chaining itself forever. `fill` stays at its default `none`, so
    // between cycles the point falls back to the CSS opacity 0 and the quiet gap
    // is genuinely dark rather than pinned at the animation's last keyframe.
    const pulse = (point: HTMLElement): Animation => {
      const animation = point.animate(
        [
          { opacity: 0, scale: "0.6" },
          { opacity: 1, scale: "1.4", offset: 0.3 },
          { opacity: 0, scale: "0.6" },
        ],
        {
          duration: sampleBetween(TWINKLE_MIN_MS, TWINKLE_MAX_MS),
          delay: sampleBetween(400, 9000),
          easing: "ease-in-out",
        },
      );
      animation.onfinish = () => {
        animation.onfinish = null;
        if (!active) return;
        const chain = running.get(point);
        if (chain) chain.twinkle = pulse(point);
      };
      return animation;
    };

    // The drift loops on its own and is created once per activation: it must not
    // be recreated when the glint chain resamples, or the slow motion would
    // reset every couple of seconds.
    const start = (point: HTMLElement): void => {
      const drift = point.animate(
        [
          { translate: "0px 0px" },
          { translate: `${sampleDrift()}px ${sampleDrift()}px`, offset: 0.5 },
          { translate: "0px 0px" },
        ],
        {
          duration: sampleBetween(DRIFT_MIN_MS, DRIFT_MAX_MS),
          delay: sampleBetween(0, 8000),
          easing: "ease-in-out",
          iterations: Infinity,
        },
      );
      running.set(point, { drift, twinkle: pulse(point) });
    };

    // Clear the completion handler BEFORE cancelling: a cancelled animation must
    // not chain its successor through a pause the synchronizer just asked for.
    const stop = (): void => {
      active = false;
      for (const [point, chain] of running) {
        chain.twinkle.onfinish = null;
        chain.twinkle.cancel();
        chain.drift.cancel();
        running.delete(point);
      }
    };

    // One owner for both gates — hidden document and reduced motion — so a
    // background/restore cycle or a live preference flip restarts each point
    // exactly once, never onto a duplicate chain.
    const sync = (): void => {
      const shouldRun = !document.hidden && !reducedQuery.matches;
      if (shouldRun === active) return;
      if (!shouldRun) {
        stop();
        return;
      }
      active = true;
      for (const point of points) start(point);
    };

    sync();
    document.addEventListener("visibilitychange", sync);
    reducedQuery.addEventListener("change", sync);
    return () => {
      document.removeEventListener("visibilitychange", sync);
      reducedQuery.removeEventListener("change", sync);
      stop();
    };
  }, []);

  return (
    <div className="atlas-specks" ref={ref} aria-hidden="true">
      {/* Positions are a fixed spread — deterministic from the index, never
          reshuffled by a render. Every third point carries the field's own
          blue-white and every fifth is a first-magnitude star. */}
      {Array.from({ length: TWINKLE_COUNT }, (_, i) => (
        <span
          key={i}
          className={twinkleClass(i)}
          style={{ left: `${(i * 37 + 11) % 100}%`, top: `${(i * 61 + 7) % 100}%` }}
        />
      ))}
    </div>
  );
}
